import * as React from 'react';
import { useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from '@emotion/styled';

import config from '../../config';
import ContentSVG from './images/content';
import OpenedSvg from './images/opened';

/**
 * Right Sidebar / Table of Contents
 * Design: Figma node-id=3-19078
 *
 * Level mapping (relative nesting):
 * - Top-level items → Level 1 (accordion with chevron)
 * - Children of top-level → Level 2 (bullet point)
 * - Children of children → Level 3 (circle dot)
 */

const Sidebar = styled('aside')`
  width: 100%;
  padding: var(--gap-4x, 20px);
  padding-top: var(--gap-20x, 100px);
  background-color: var(--color-bg-container);

  .rightSideTitle {
    display: flex;
    align-items: center;
    gap: var(--gap-2x, 10px);
    margin-bottom: var(--gap-3x, 15px);
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: 13px;
    font-weight: 600;
    color: var(--color-info-tertiary);

    .rightSideTitleIcon {
      width: 18px;
      height: 18px;
      color: var(--color-info-tertiary);
    }
  }
`;

const TocWrapper = styled('div')`
  position: relative;
  padding-left: 8px;

  /* Vertical trail line */
  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: var(--color-bg-box);
  }
`;

const TocList = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TocItemWrapper = styled('li')`
  position: relative;
  list-style: none;
`;

const TocLink = styled('a')`
  display: flex;
  align-items: center;
  gap: var(--gap-2x, 10px);
  text-decoration: none;
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--color-info-tertiary);
  transition: color var(--transition-fast, 0.15s ease);
  position: relative;
  min-height: 25px;
  --border-left-width: 2px;
  border-left: var(--border-left-width) solid transparent;


  /* Level-based padding */
  padding-left: ${props => {
    if (props.level === 1) return 'calc(25px - var(--border-left-width))';
    if (props.level === 2) return 'calc(30px - var(--border-left-width))';
    return 'calc(45px - var(--border-left-width))';
  }};
  padding-right: var(--gap-1x, 5px);
  padding-top: ${props => props.level === 1 ? '0' : '3px'};
  padding-bottom: ${props => props.level === 1 ? '0' : '3px'};

  /* Active state - green left border */
  ${props => props.isActive && `
    border-color: var(--color-primary);
    color: var(--color-primary);

    &:hover {
      color: var(--color-primary-hover);
    }
  `}

  ${props => !props.isActive && `&:hover {
    color: var(--color-info-secondary);
  }`}
`;

const TocText = styled('span')`
  flex: 1;

  /* Level 2 - bullet point */
  ${props => props.level === 2 && `
    &::before {
      content: '•';
      margin-right: 8px;
    }
  `}
`;

const CollapseButton = styled.button`
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  transition: transform var(--transition-fast, 0.15s ease);
  flex-shrink: 0;
  width: 12px;
  height: 12px;

  svg {
    width: 12px;
    height: 12px;
    color: inherit;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const ChildrenList = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

/**
 * TocItem component - renders a single TOC item with proper level styling
 */
const TocItem = ({ item, level, activeId, setActiveId, collapsed, setCollapsed }) => {
  const hasChildren = item.items && item.items.length > 0;
  const itemId = item.title ? item.title.replace(/\s+/g, '').toLowerCase() : '';
  const isActive = activeId === itemId;
  const isCollapsed = collapsed[itemId];

  const handleClick = (e) => {
    e.preventDefault();
    setActiveId(itemId);

    // Scroll to anchor
    const element = document.getElementById(itemId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Update URL hash without jumping
      window.history.pushState(null, '', `#${itemId}`);
    }
  };

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCollapsed(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  return (
    <TocItemWrapper>
      <TocLink
        href={`#${itemId}`}
        level={level}
        isActive={isActive}
        onClick={handleClick}
      >
        <TocText level={level}>{item.title}</TocText>
        {level === 1 && hasChildren && (
          <CollapseButton
            onClick={handleToggle}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            aria-expanded={!isCollapsed}
            style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          >
            <OpenedSvg />
          </CollapseButton>
        )}
      </TocLink>

      {hasChildren && !isCollapsed && level < 3 && (
        <ChildrenList>
          {item.items.map((childItem, index) => (
            <TocItem
              key={`${level + 1}-${index}`}
              item={childItem}
              level={level + 1}
              activeId={activeId}
              setActiveId={setActiveId}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
            />
          ))}
        </ChildrenList>
      )}
    </TocItemWrapper>
  );
};

/**
 * Main Table of Contents component
 */
const TableOfContents = ({ items }) => {
  const [activeId, setActiveId] = useState('');
  const [collapsed, setCollapsed] = useState({});

  // Detect active anchor from URL hash on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setActiveId(hash);
      }
    }
  }, []);

  // Listen for hash changes (e.g., browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      setActiveId(hash);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <TocWrapper>
      <TocList>
        {items.map((item, index) => (
          <TocItem
            key={`1-${index}`}
            item={item}
            level={1}
            activeId={activeId}
            setActiveId={setActiveId}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
        ))}
      </TocList>
    </TocWrapper>
  );
};

const SidebarLayout = ({ location }) => {
  const data = useStaticQuery(graphql`
    query {
      allMdx {
        edges {
          node {
            fields {
              slug
            }
            tableOfContents
          }
        }
      }
    }
  `);

  const [tocItems, setTocItems] = useState(null);

  useEffect(() => {
    // Get pathname from window.location which is always available client-side
    if (typeof window === 'undefined') return;

    let pathname = window.location.pathname;

    // Ensure pathname doesn't have trailing slash for comparison (unless it's root)
    if (pathname !== '/' && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }

    // Find matching TOC
    let foundTocItems = null;
    const allMdx = data.allMdx;

    if (allMdx.edges !== undefined && allMdx.edges.length > 0) {
      allMdx.edges.forEach((item) => {
        if (item !== undefined) {
          let slug = item.node.fields.slug || '';
          // Normalize slug - remove trailing slash unless it's root
          if (slug !== '/' && slug.endsWith('/')) {
            slug = slug.slice(0, -1);
          }

          const prefixedSlug = config.gatsby.pathPrefix + slug;


          if (slug === pathname || prefixedSlug === pathname) {
            if (item.node.tableOfContents && item.node.tableOfContents.items) {
              foundTocItems = item.node.tableOfContents.items;
            }
          }
        }
      });
    }

    setTocItems(foundTocItems);
  }, [location, data.allMdx]);

  if (tocItems && tocItems.length) {
    return (
      <Sidebar>
        <div className="rightSideTitle">
          <ContentSVG className="rightSideTitleIcon" />
          <span>Table Of Contents</span>
        </div>
        <TableOfContents items={tocItems} />
      </Sidebar>
    );
  } else {
    return (
      <Sidebar>
        <ul></ul>
      </Sidebar>
    );
  }
};

export default SidebarLayout;
